import React, { useState, useEffect } from 'react';
import { HeartIcon as HeartOutline } from '@heroicons/react/24/outline';
import { HeartIcon as HeartSolid } from '@heroicons/react/24/solid';
import { useAuth } from '../../context/AuthContext';
import { addToFavorites, removeFromFavorites, isFavorite } from '../../lib/db';
import { checkAuthStatus } from '../../lib/supabase';

const FavoriteButton = ({ 
  itemId, 
  itemType, 
  size = 'md', 
  className = '',
  showTooltip = true,
  onToggle = null 
}) => {
  const { user } = useAuth();
  const [isFavorited, setIsFavorited] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [authError, setAuthError] = useState(null);

  // Check auth status on mount
  useEffect(() => {
    const verifyAuth = async () => {
      const { session, error } = await checkAuthStatus();
      if (error) {
        setAuthError(error.message);
      }
    };

    verifyAuth();
  }, []);

  // Check if item is favorited on mount
  useEffect(() => {
    const checkFavoriteStatus = async () => {
      if (user?.id && itemId && itemType) {
        // Verify auth status before checking favorite status
        const { session } = await checkAuthStatus();
        if (!session) {
          return;
        }

        const favorited = await isFavorite(user.id, itemType, itemId);
        setIsFavorited(favorited);
      }
    };

    checkFavoriteStatus();
  }, [user?.id, itemId, itemType]);

  const handleToggleFavorite = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!user?.id) {
      return;
    }

    // Verify auth status before toggling
    const { session } = await checkAuthStatus();
    if (!session) {
      return;
    }

    setIsLoading(true);

    try {
      let result;
      if (isFavorited) {
        result = await removeFromFavorites(user.id, itemType, itemId);
      } else {
        result = await addToFavorites(user.id, itemType, itemId);
      }

      if (result.success) {
        setIsFavorited(!isFavorited);
        setShowFeedback(true);
        setTimeout(() => setShowFeedback(false), 1500);
        
        // Call onToggle callback if provided
        if (onToggle) {
          onToggle(!isFavorited, itemId, itemType);
        }
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Size configurations
  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6'
  };

  const buttonSizeClasses = {
    sm: 'p-1',
    md: 'p-1.5',
    lg: 'p-2'
  };

  if (!user?.id) {
    // For debugging - show a disabled heart for non-authenticated users
    return (
      <div className="relative">
        <button
          disabled
          className={`
            relative transition-all duration-200 rounded-full opacity-50
            ${buttonSizeClasses[size]}
            text-gray-400
          `}
          title="Please sign in to favorite items"
        >
          <HeartOutline className={sizeClasses[size]} />
        </button>
      </div>
    );
  }

  return (
    <div className="relative">
      <button
        onClick={handleToggleFavorite}
        disabled={isLoading}
        className={`
          relative transition-all duration-200 rounded-full bg-black/50 border border-white/20
          ${buttonSizeClasses[size]}
          ${isFavorited 
            ? 'text-red-500 hover:text-red-600 hover:scale-110' 
            : 'text-white hover:text-red-500 hover:scale-110'
          }
          ${isLoading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-800/70'}
          ${className}
        `}
        title={showTooltip ? (isFavorited ? 'Remove from favorites' : 'Add to favorites') : ''}
      >
        {isFavorited ? (
          <HeartSolid className={`${sizeClasses[size]} animate-pulse`} />
        ) : (
          <HeartOutline className={sizeClasses[size]} />
        )}
        
        {/* Loading spinner overlay */}
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-3 h-3 border border-gray-400 border-t-transparent rounded-full animate-spin"></div>
          </div>
        )}
      </button>

      {/* Feedback animation */}
      {showFeedback && (
        <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 pointer-events-none">
          <div className="bg-gray-800 text-white text-xs px-2 py-1 rounded-md shadow-lg animate-bounce">
            {isFavorited ? '❤️ Added!' : '💔 Removed!'}
          </div>
        </div>
      )}
    </div>
  );
};

export default FavoriteButton; 